import { formatLocaleLabel } from './labels'

interface CountryIdentity {
  name: string
  code: string
}

interface CountryLocales {
  locales: readonly string[]
}

export interface LocaleOption {
  locale: string
  label: string
  flagUri: string
}

export function findCountryByCodeOrName<TCountry extends CountryIdentity>(
  countries: readonly TCountry[],
  codeOrName: string,
): TCountry | undefined {
  const key = codeOrName.trim().toLowerCase()
  return countries.find(
    (country) => country.code.toLowerCase() === key || country.name.toLowerCase() === key,
  )
}

/** Match the lowercased requested locale against the supplied locale list, as before. */
export function findCountryByLocale<TCountry extends CountryLocales>(
  countries: readonly TCountry[],
  locale: string,
): TCountry | undefined {
  return countries.find((country) => country.locales.includes(locale.toLowerCase()))
}

export function getCurrencyCodeForCountry<TCountry extends CountryIdentity & { currency: string }>(
  countries: readonly TCountry[],
  countryCodeOrName: string,
): TCountry['currency'] | undefined {
  return findCountryByCodeOrName(countries, countryCodeOrName)?.currency
}

export function getDefaultLocaleForCountry<
  TCountry extends CountryIdentity & CountryLocales & { defaultLanguage: string },
>(countries: readonly TCountry[], countryCodeOrName: string): string | null {
  const country = findCountryByCodeOrName(countries, countryCodeOrName)
  const locales = country?.locales
  if (!locales?.length) return null
  const defaultLang = country?.defaultLanguage
  return (
    locales.find((locale) => locale.startsWith(`${defaultLang}-`) || locale === defaultLang) ??
    locales[0]
  )
}

export function getLocaleOptionsForCountries<
  TCountry extends {
    code: string
    locales: readonly string[]
    flagImageUri: { rectangle: string }
  },
>(countries: readonly TCountry[], countryCodes: readonly string[]): LocaleOption[] {
  return countryCodes
    .map((code) => countries.find((country) => country.code.toLowerCase() === code.toLowerCase()))
    .filter((country) => country !== undefined)
    .flatMap((country) =>
      country.locales.map((locale) => ({
        locale,
        label: formatLocaleLabel(locale),
        flagUri: country.flagImageUri.rectangle,
      })),
    )
}
